import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarCheck, Coffee, House, ListChecks, CheckCircle, Gift, Sparkle,
} from "@phosphor-icons/react";

/* ----------------- Tile 1: Holborough (LIVE) — Appistry blue/teal ---------------- */
function LiveHolboroughTile() {
  const [count, setCount] = useState(23);
  useEffect(() => {
    const id = setInterval(() => setCount((c) => (c >= 29 ? 23 : c + 1)), 2600);
    return () => clearInterval(id);
  }, []);
  const heights = [40, 70, 55, 85, 60, 95, 75];
  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2.5 text-white" style={{ background: "linear-gradient(135deg, #0070F3, #00C4CC)" }}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[8px] font-mono uppercase tracking-[0.25em] opacity-90">Holborough</span>
          <span className="flex items-center gap-1 text-[8px] font-mono">
            <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> LIVE
          </span>
        </div>
        <div className="text-[11px] font-black leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Summer BBQ</div>
        <div className="text-[9px] opacity-80 font-mono">Sat 14 Aug</div>
      </div>
      <div className="flex-1 p-2.5 bg-white">
        <div className="flex items-baseline gap-2 mb-1">
          <motion.div
            key={count}
            initial={{ scale: 1.2, color: "#0070F3" }}
            animate={{ scale: 1, color: "#0A0A0A" }}
            transition={{ duration: 0.35 }}
            className="text-xl font-black leading-none"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {count}
          </motion.div>
          <div className="text-[9px] text-zinc-500 font-mono">/ 30 going</div>
        </div>
        <div className="flex -space-x-1 mb-2">
          {["#0070F3", "#00C4CC", "#C6392E", "#0F3024"].map((c, i) => (
            <div key={i} className="w-4 h-4 rounded-full border border-white text-white text-[7px] font-bold flex items-center justify-center" style={{ backgroundColor: c }}>
              {["A", "B", "C", "D"][i]}
            </div>
          ))}
          <div className="w-4 h-4 rounded-full border border-white bg-zinc-100 text-[7px] font-bold flex items-center justify-center text-zinc-500">+{count - 4}</div>
        </div>
        <div className="flex items-end gap-0.5 h-5">
          {heights.map((h, i) => (
            <motion.div
              key={i}
              initial={{ height: 0 }}
              animate={{ height: `${h}%` }}
              transition={{ delay: 0.2 + i * 0.05, duration: 0.6 }}
              className="flex-1"
              style={{ background: i === heights.length - 1 ? "#0070F3" : "#DBEAFE" }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ----------------- Tile 2: Pop-up Public Board — bone + forest + cherry (LIVE) ---------------- */
function PopupTile() {
  // Stock ticks down on Cinnamon Bun; Lemon Drizzle flips back to "IN STOCK" once restocked
  const [cinnamon, setCinnamon] = useState(8);
  const [lemonSold, setLemonSold] = useState(true);
  useEffect(() => {
    const id = setInterval(() => {
      setCinnamon((c) => (c <= 3 ? 8 : c - 1));
      setLemonSold((s) => (Math.random() > 0.7 ? !s : s));
    }, 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="h-full flex flex-col" style={{ background: "#F5F1E8" }}>
      <div className="px-3 pt-2 pb-1.5 text-center border-b relative" style={{ borderColor: "#E8E2D1" }}>
        <div className="text-[7px] font-mono uppercase tracking-[0.3em]" style={{ color: "#1F3A2A" }}>Birch &amp; Bean · Today</div>
        <div className="text-[13px] font-black leading-tight mt-0.5" style={{ fontFamily: "'Outfit', sans-serif", color: "#0F1A14" }}>On the board</div>
        <span className="absolute top-1.5 right-1.5 flex items-center gap-0.5 text-[7px] font-mono" style={{ color: "#1F3A2A" }}>
          <span className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: "#C6392E" }} /> LIVE
        </span>
      </div>
      <div className="flex-1 p-2 flex flex-col justify-between">
        {/* Cinnamon Bun — live stock */}
        <div className="flex items-center gap-1.5 py-1">
          <div className="text-sm flex-shrink-0">🥯</div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] font-bold truncate leading-tight" style={{ color: "#0F1A14" }}>Cinnamon Bun</div>
          </div>
          <motion.span key={cinnamon} initial={{ scale: 1.2, color: "#C6392E" }} animate={{ scale: 1, color: "#1F3A2A" }} transition={{ duration: 0.35 }} className="text-[7px] font-mono font-bold">{cinnamon} LEFT</motion.span>
        </div>
        {/* Lemon Drizzle — flips sold-out ↔ in-stock */}
        <div className="flex items-center gap-1.5 py-1">
          <div className="text-sm flex-shrink-0" style={{ opacity: lemonSold ? 0.4 : 1 }}>🍋</div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] font-bold truncate leading-tight" style={{ color: "#0F1A14", textDecoration: lemonSold ? "line-through" : "none", opacity: lemonSold ? 0.5 : 1 }}>Lemon Drizzle</div>
          </div>
          {lemonSold ? (
            <motion.span key="out" initial={{ scale: 1.1 }} animate={{ scale: 1 }} className="text-[7px] font-mono font-bold px-1 py-0.5 text-white" style={{ background: "#C6392E" }}>SOLD OUT</motion.span>
          ) : (
            <motion.span key="in" initial={{ scale: 1.1 }} animate={{ scale: 1 }} className="text-[7px] font-mono font-bold" style={{ color: "#1F3A2A" }}>IN STOCK</motion.span>
          )}
        </div>
        {/* Special */}
        <div className="flex items-center gap-1.5 py-1 border-t" style={{ borderColor: "#E8E2D1" }}>
          <div className="text-sm flex-shrink-0" style={{ color: "#C6392E" }}>✦</div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] font-bold truncate leading-tight" style={{ color: "#0F1A14" }}>Pecan Brownie + Oat Latte</div>
          </div>
          <span className="text-[7px] font-mono font-bold px-1 py-0.5 text-white" style={{ background: "#C6392E" }}>SPECIAL</span>
        </div>
      </div>
    </div>
  );
}

/* ----------------- Tile 3: Open-House viewing — ink + chalk + petrol ---------------- */
function ViewingTile() {
  // Compact listing card — real photo thumb + HOT chip + price + signal strip
  return (
    <div className="h-full flex flex-col" style={{ background: "#F2F4F7" }}>
      <div className="flex-1 flex flex-col bg-white border" style={{ borderColor: "#D9DEE5" }}>
        <div className="flex items-stretch">
          <div className="relative w-14 flex-shrink-0 overflow-hidden bg-slate-200">
            <img src="https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=160&h=200&auto=format&fit=crop" alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute top-0.5 left-0.5 flex items-center gap-0.5 px-1 text-[6px] font-mono uppercase tracking-widest font-bold text-white" style={{ backgroundColor: "#10B981" }}>
              <span className="w-1 h-1 rounded-full bg-white/90" />HOT
            </div>
          </div>
          <div className="flex-1 min-w-0 p-2">
            <div className="text-[7px] font-mono uppercase tracking-widest opacity-60">HOLBOROUGH</div>
            <div className="text-[10px] font-black leading-tight mt-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>12 Lakeside Walk</div>
            <div className="text-[7px] opacity-60 mt-0.5">3-bed · Semi-detached</div>
            <div className="text-sm font-black mt-1 tracking-tight leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>£485k</div>
            <div className="text-[7px] font-mono font-bold mt-0.5" style={{ color: "#10B981" }}>top offer £470k</div>
          </div>
        </div>
        <div className="px-2 py-1 border-t text-[8px] italic flex items-center gap-1" style={{ borderColor: "#D9DEE5", color: "#10B981", fontFamily: "'Outfit', serif" }}>
          <span className="w-1 h-1 rounded-full" style={{ backgroundColor: "#10B981" }} />
          1 offer · move to decision
        </div>
      </div>
    </div>
  );
}

/* ----------------- Tile 4: Stamp Card — mirrors the real app phone layout (LIVE) ---------------- */
function StampTile() {
  const [stamps, setStamps] = useState(7);
  const [justStamped, setJustStamped] = useState(-1);
  useEffect(() => {
    const id = setInterval(() => {
      setStamps((s) => {
        const next = s >= 10 ? 7 : s + 1;
        setJustStamped(next - 1);
        setTimeout(() => setJustStamped(-1), 600);
        return next;
      });
    }, 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="h-full flex flex-col" style={{ background: "#F5EFE6" }}>
      <div className="px-3 py-2 relative" style={{ background: "#0A0A0A", color: "white" }}>
        <div className="flex items-center gap-1.5">
          <Coffee size={9} weight="fill" style={{ color: "#E94E3B" }} />
          <span className="text-[7px] font-mono uppercase tracking-[0.3em] opacity-80">Birch &amp; Bean</span>
        </div>
        <div className="text-[13px] font-black leading-tight mt-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>Coffee Club</div>
        <span className="absolute top-1.5 right-1.5 flex items-center gap-0.5 text-[7px] font-mono opacity-70">
          <span className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: "#E94E3B" }} /> LIVE
        </span>
      </div>
      <div className="flex-1 p-2 flex flex-col justify-between">
        <div className="grid grid-cols-5 gap-1 mt-1">
          {[...Array(10)].map((_, i) => {
            const on = i < stamps;
            const isJust = i === justStamped;
            return (
              <motion.div
                key={i}
                animate={isJust ? { scale: [1, 1.35, 1] } : { scale: 1 }}
                transition={{ duration: 0.5 }}
                className="aspect-square rounded-full flex items-center justify-center border"
                style={{ borderColor: on ? "#E94E3B" : "#E5DFD5", background: on ? "#E94E3B" : "white" }}
              >
                {on && <Coffee size={6} weight="fill" color="white" />}
              </motion.div>
            );
          })}
        </div>
        <div className="text-center mt-1">
          <motion.div
            key={stamps}
            initial={{ scale: 1.2, color: "#E94E3B" }}
            animate={{ scale: 1, color: "#0A0A0A" }}
            transition={{ duration: 0.35 }}
            className="text-base font-black leading-none"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {stamps}<span className="text-[9px] text-zinc-400 font-bold"> / 10</span>
          </motion.div>
          <div className="text-[7px] font-mono uppercase tracking-widest mt-0.5" style={{ color: "#E94E3B" }}>✦ 2× STAMPS TODAY</div>
        </div>
      </div>
    </div>
  );
}

/* ----------------- Tile 5: Action Portal — bottle-green + white ---------------- */
function ActionTile() {
  const tasks = [
    { who: "R", name: "Playground plans", status: "open", color: "#0F3024" },
    { who: "S", name: "Summer BBQ brief", status: "open", color: "#1E5463" },
    { who: "M", name: "Recycling audit", status: "done", color: "#C6392E" },
  ];
  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2 flex items-center justify-between" style={{ background: "#0F3024", color: "white" }}>
        <div className="flex items-center gap-1.5">
          <ListChecks size={11} weight="fill" />
          <span className="text-[8px] font-mono uppercase tracking-[0.25em]">HLRA</span>
        </div>
        <span className="text-[7px] font-mono opacity-70">3 OPEN</span>
      </div>
      <div className="flex-1 p-2 bg-white space-y-1">
        {tasks.map((t, i) => (
          <div key={i} className="flex items-center gap-1.5 p-1 border" style={{ borderColor: "#E5E7EB" }}>
            <div className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[7px] font-bold flex-shrink-0" style={{ background: t.color }}>{t.who}</div>
            <div className="flex-1 min-w-0 text-[9px] truncate">{t.name}</div>
            {t.status === "done"
              ? <CheckCircle size={10} weight="fill" className="text-emerald-600 flex-shrink-0" />
              : <span className="text-[7px] font-mono font-bold" style={{ color: "#0F3024" }}>OPEN</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----------------- Tile 6: Voucher Vault — ink + electric coral (modern) ---------------- */
function VoucherTile() {
  return (
    <div className="h-full flex flex-col p-1.5" style={{ background: "#F7F5F1" }}>
      <div className="flex-1 relative overflow-hidden p-3" style={{ background: "#0F1014", color: "white" }}>
        {/* coral corner slash */}
        <div className="absolute top-0 right-0 w-10 h-10" style={{ background: "linear-gradient(135deg, transparent 50%, #FF5A3C 50%)" }} />
        <div className="absolute inset-2 border" style={{ borderColor: "#F4E4B066" }} />
        <div className="relative flex flex-col h-full">
          <div className="flex items-center justify-between">
            <span className="text-[7px] font-mono uppercase tracking-[0.3em]" style={{ color: "#F4E4B0" }}>Gift Voucher</span>
            <Sparkle size={10} weight="fill" style={{ color: "white" }} />
          </div>
          <div className="text-[8px] font-bold mt-0.5 italic" style={{ fontFamily: "'Outfit', serif" }}>Pennyweight &amp; Co.</div>
          <div className="mt-auto">
            <div className="text-2xl font-black leading-none tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif" }}>£100</div>
            <div className="text-[8px] italic mt-1 opacity-80" style={{ fontFamily: "'Outfit', serif" }}>"Happy birthday, Mum"</div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[7px] font-mono opacity-80">VV-9K2-7PQ</span>
              <div className="w-4 h-4 bg-white flex items-center justify-center">
                <Gift size={8} weight="fill" style={{ color: "#0F1014" }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const TILES = [
  { id: 1, rotate: -7, slug: "holborough-social", label: "Holborough Social", element: <LiveHolboroughTile /> },
  { id: 2, rotate: 5, slug: "popup-prep", label: "Pop-up Board", element: <PopupTile /> },
  { id: 3, rotate: -3, slug: "open-house-tracker", label: "Open-House Tracker", element: <ViewingTile /> },
  { id: 4, rotate: 8, slug: "stamp-card", label: "Stamp Card", element: <StampTile /> },
  { id: 5, rotate: -5, slug: "action-management-portal", label: "Action Portal", element: <ActionTile /> },
  { id: 6, rotate: 4, slug: "voucher-vault", label: "Voucher Vault", element: <VoucherTile /> },
];

export default function HeroMosaic() {
  return (
    <div className="relative h-[480px] w-full" data-testid="hero-mosaic">
      <div className="absolute inset-0 grid grid-cols-3 gap-4 p-1">
        {TILES.map((t, i) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 24, rotate: t.rotate - 4 }}
            animate={{ opacity: 1, y: 0, rotate: t.rotate }}
            transition={{ delay: 0.1 * i, duration: 0.7, ease: "easeOut" }}
            whileHover={{ rotate: 0, scale: 1.06, zIndex: 10, transition: { duration: 0.3 } }}
            className="shadow-lg border relative group"
            style={{ borderColor: "#E5E7EB", transformOrigin: "center" }}
          >
            <Link to={`/demos/${t.slug}`} className="block h-full overflow-hidden" data-testid={`hero-tile-${t.id}`}>
              {t.element}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent text-white text-[9px] font-mono uppercase tracking-[0.2em] text-center py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                View {t.label} →
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
